import React from 'react';
import { Brain, FileText, Search, Zap, Shield, Globe, ArrowRight } from 'lucide-react';

const LandingPage = ({ onLoginClick }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <img src="/codings-logo.png" alt="CODINGS Logo" className="h-24 w-24 object-contain animate-pulse" />
                <div className="absolute inset-0 bg-primary-200 rounded-full blur-xl opacity-50"></div>
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              CODINGS
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-4">
              Contextual Ontology-Driven Document Intelligence
            </p>
            <p className="text-lg text-gray-500 mb-8">
              and Narrative Generation System
            </p>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-10 leading-relaxed">
              Transform your research papers and documents into intelligent, searchable knowledge bases.
              Powered by cutting-edge AI models for semantic understanding and comprehensive analysis.
            </p>
            <button
              onClick={onLoginClick}
              className="inline-flex items-center px-8 py-4 bg-primary-600 text-white text-lg font-semibold rounded-lg hover:bg-primary-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Powerful Features
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <FileText className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Document Processing</h3>
            <p className="text-gray-600">
              Upload PDFs, text files, or markdown documents. Our AI automatically extracts,
              chunks, and structures your content for intelligent analysis.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Search className="h-8 w-8 text-indigo-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Advanced RAG Search</h3>
            <p className="text-gray-600">
              Ask natural language questions and get precise answers. Our multi-model AI
              system combines document understanding with web search for comprehensive results.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Zap className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Model AI Analysis</h3>
            <p className="text-gray-600">
              Leverage multiple AI models (Sonar, Grok, GPT-4o Mini, Gemini) working in parallel
              to provide diverse perspectives and synthesized insights.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Brain className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Intelligent Summarization</h3>
            <p className="text-gray-600">
              Get structured summaries organized by research sections - from methodology
              to results, automatically categorized and summarized.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Privacy-First</h3>
            <p className="text-gray-600">
              All processing happens in-memory. Your documents are processed securely
              and never permanently stored on servers.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Globe className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Web-Enhanced Answers</h3>
            <p className="text-gray-600">
              Combine your document insights with real-time web information for
              comprehensive, up-to-date answers with source citations.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Upload</h3>
              <p className="text-gray-600 text-sm">Upload your PDF or document</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Process</h3>
              <p className="text-gray-600 text-sm">AI extracts and chunks content</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Search</h3>
              <p className="text-gray-600 text-sm">Ask questions in natural language</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                4
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Analyze</h3>
              <p className="text-gray-600 text-sm">Get AI-powered insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-primary-600 to-indigo-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Documents?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Experience the power of AI-driven document intelligence
          </p>
          <button
            onClick={onLoginClick}
            className="inline-flex items-center px-8 py-4 bg-white text-primary-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Analyzing Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-600">
            <p className="mb-2">© 2024 CODINGS. Powered by AI embeddings and semantic search.</p>
            <p className="text-sm">
              Credits: {' '}
              <a
                href="https://www.linkedin.com/in/sankar4"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Sankar Balasubramanian
              </a>
              {' '}•{' '}
              <a
                href="https://sankar.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                sankar.studio
              </a>
              {' '}and{' '}
              <a
                href="https://www.linkedin.com/in/aayyushi-jain/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Aayushi Jain
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

